// MYG-151497: turns the token set from the device authorization grant into what the existing IAM login
// (IamFlow) expects after a popup sign-in: an oidc-client-ts User plus the IAM settings of the realm/client.

import { User } from "oidc-client-ts";
import type { UserProfile } from "oidc-client-ts";
import type { IIamSettings } from "../../../../utils/auth/iamSettings";
import type { IIamAppState } from "../../../../services/iamService";
import { IAM_PROVIDER_TYPE_KEYCLOAK, IDeviceAuthTokens } from "./deviceAuthClient";

export interface IDeviceAuthSessionContext {
    /** The realm URL and client the device request was made with. The tokens must come from exactly these. */
    issuer: string;
    clientId: string;
    language: string;
    appState: IIamAppState;
    /** Injectable for tests; defaults to the current time. */
    nowMs?: number;
}

export interface IDeviceAuthSession {
    user: User;
    username: string;
    iamSettings: IIamSettings;
}

/** Decodes a JWT payload without verifying it. The server verifies the token; this is only to read claims. */
export function decodeJwtPayload(token: string | undefined): Record<string, unknown> | undefined {
    const segment = token?.split(".")[1];
    if (!segment) {
        return undefined;
    }

    try {
        const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
        const json = decodeURIComponent(window.atob(base64).split("").map(char => `%${ (`00${ char.charCodeAt(0).toString(16) }`).slice(-2) }`).join(""));
        const payload = JSON.parse(json);
        return payload && typeof payload === "object" ? payload : undefined;
    } catch {
        return undefined;
    }
}

function asString(value: unknown): string | undefined {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function createDeviceAuthSession(tokens: IDeviceAuthTokens, context: IDeviceAuthSessionContext): IDeviceAuthSession {
    // The id token is what oidc-client-ts would use for the profile; fall back to the access token.
    const claims = decodeJwtPayload(tokens.id_token) || decodeJwtPayload(tokens.access_token);
    if (!claims) {
        throw new Error("Device sign-in returned tokens that could not be read.");
    }

    const accessClaims = decodeJwtPayload(tokens.access_token) || claims;
    const tokenIssuer = asString(claims.iss) || asString(accessClaims.iss);
    const tokenClientId = asString(accessClaims.azp) || asString(claims.azp);
    const username = asString(claims.preferred_username) || asString(accessClaims.preferred_username) || asString(claims.email);
    const subject = asString(claims.sub);

    if (!tokenIssuer || !tokenClientId || !username || !subject) {
        throw new Error("Device sign-in tokens are missing the issuer, client, user or subject claim.");
    }

    // The IAM settings decide the token store key and which client refreshes and revokes later, so they come from
    // what we asked for, never from the token. A token that names another issuer or client is not ours to store.
    if (tokenIssuer.replace(/\/+$/, "") !== context.issuer.replace(/\/+$/, "") || tokenClientId !== context.clientId) {
        throw new Error("Device sign-in tokens were not issued for the requested realm and client.");
    }

    const nowMs = context.nowMs ?? Date.now();
    const user = new User({
        id_token: tokens.id_token,
        session_state: null,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type || "Bearer",
        scope: tokens.scope,
        profile: { ...claims, sub: subject } as UserProfile,
        // oidc-client-ts stores expires_at in Unix seconds.
        expires_at: Math.floor(nowMs / 1000) + (tokens.expires_in || 0),
        userState: context.appState
    });

    return {
        user,
        username,
        iamSettings: { iamProviderType: IAM_PROVIDER_TYPE_KEYCLOAK, loginUrl: context.issuer, clientId: context.clientId, language: context.language }
    };
}
